import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {
	AddSignature,
	AdminWebserviceStatus,
	AnnotationRectangle,
	AppearanceAdd,
	AppearanceAddInterface,
	CertificateEntry,
	CertificateFileData,
	CertificationLevel,
	DocMdp,
	Field,
	FormDocument,
	Functions,
	Information,
	KeyPair,
	KeyPairInterface,
	MetadataInformation,
	MetadataRectangle,
	PrivateKeyFileData,
	Security,
	Settings,
	SettingsInterface,
	Signature,
	SignatureEditProfile,
	SignatureIdentifier,
	SignatureImage,
	SignaturePosition,
	SignerAdd,
	SignerAddInterface,
	UserCertificates
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {TabPanel, TabView} from "primereact/tabview";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {File} from "../../../../shared/components/FileInput";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import SignatureDialogFields from "../../field/signature/SignatureDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import SignatureAddCertificateDialog from "./SignatureAddCertificateDialog";
import {PortalAddSignature} from "../../../../shared/models/PortalAddSignature";
import {PortalSignatureFileData} from "../../../../shared/models/PortalSignatureFileData";
import {Password} from "primereact/password";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import {PortalSignature} from "../../../../shared/models/PortalSignature";
import Helper from "../../../../shared/components/Helper";
import SignatureAppearanceDialog from "./SignatureAppearanceDialog";
import {MultiSelect} from "primereact/multiselect";
import {InputSwitch} from "primereact/inputswitch";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface SignatureDialogProperties extends PortalDialogProperties<PortalSignature> {
	userCertificates: Immutable<UserCertificates>;
}

interface SignatureDialogStatus extends PortalDialogState<PortalSignature> {
	panel: number;
	showSelection: boolean;
	showAddDialog: boolean;
	showAppearanceDialog: boolean;
	certifcatePasswordNeeded: boolean;
	imageFile: File;
	signatures: Array<Field>;
}

interface CertificateOption {
	label?: string;
	value?: string;
	disabled?: boolean;
	className?: string;
	password?: boolean;
	separator?: boolean;
	keyStoreName?: string;
}

interface SignatureOption {
	label: string;
	value?: string;
	disabled?: boolean;
	className?: string;
}

export class SignatureDialogDefinition extends PortalDialog<PortalSignature, SignatureDialogProperties,
	SignatureDialogStatus> {
	private readonly SignatureOptionNew = "";
	private readonly CertificateOptionNew = "";

	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			panel: 0,
			signatures: [],
			imageFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			showSelection: true,
			showAddDialog: false,
			showAppearanceDialog: false,
			certifcatePasswordNeeded: false,
			fieldErrors: new Map<SignatureDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.signature?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.signDocument, Functions.getSignDocumentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.SIGNATURE, signatureEditProfile, Intention.signingDocuments, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderGeneralTab(parameter: PortalAddSignature): ReactNode {
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		if (!FunctionHelper.exists(portalDocument)) {
			return null;
		}

		let isCertified: boolean = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.docMDP, MetadataInformation.getDocMDPDefault()
		) !== DocMdp.None;

		let certificates: Array<CertificateOption> = [
			{
				label: Translate.value("signature.general.signature.certificate.manual", {ns: "dialog"}),
				value: this.CertificateOptionNew
			},
			{separator: true, className: "separator", disabled: true}
		];

		let userCertificates = UserCertificates.fromJson(this.props.userCertificates.get());
		for (let certificate of FunctionHelper.applyValue(
			userCertificates.certificates, UserCertificates.getCertificatesDefault()
		)) {
			let parsedCertificate: x509.X509Certificate | undefined;

			try {
				parsedCertificate = new x509.X509Certificate(certificate.certificate);
			} catch (e: any) {
				continue;
			}

			if (certificate.hasPrivateKey) {
				certificates.push({
					label: parsedCertificate.subject,
					value: certificate.aliasName,
					password: certificate.hasPrivateKey && !certificate.isPrivateKeyReadable,
					keyStoreName: certificate.keyStoreName
				});
			}
		}

		let signatures: Array<SignatureOption> = [
			{
				label: Translate.value("signature.general.signature.field.new", {ns: "dialog"}),
				value: this.SignatureOptionNew
			}
		];
		for (let signature of this.state.signatures) {
			signatures.push({label: signature.name!, value: signature.name!});
		}

		let errors: Map<SignatureDialogFields, string> = this.state.fieldErrors;

		let certificate: x509.X509Certificate | undefined;
		if (FunctionHelper.applyValue(parameter.signer?.keyPair?.certificate?.value, "") !== "") {
			try {
				certificate = new x509.X509Certificate(parameter.signer?.keyPair?.certificate?.value);
			} catch (e: any) {
				// can be ignored here
			}
		}

		let identifier: Map<SignatureDialogFields, boolean> = new Map<SignatureDialogFields, boolean>([
			[SignatureDialogFields.PERSONAL_SIGNED_BY, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showSignedBy, SignatureIdentifier.getShowSignedByDefault()
			)],
			[SignatureDialogFields.PERSONAL_NAME, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showName, SignatureIdentifier.getShowNameDefault()
			)],
			[SignatureDialogFields.PERSONAL_EMAIL, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showMail, SignatureIdentifier.getShowMailDefault()
			)],
			[SignatureDialogFields.ORGANISATION_COMMON_NAME, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showCommonName, SignatureIdentifier.getShowCommonNameDefault()
			)],
			[SignatureDialogFields.ORGANISATION_UNIT, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showOrganisationUnit,
				SignatureIdentifier.getShowOrganisationUnitDefault()
			)],
			[SignatureDialogFields.ORGANISATION_NAME, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showOrganisationName,
				SignatureIdentifier.getShowOrganisationNameDefault()
			)],
			[SignatureDialogFields.ENVIRONMENT_STATE, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showState, SignatureIdentifier.getShowStateDefault()
			)],
			[SignatureDialogFields.ENVIRONMENT_COUNTRY, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showCountry, SignatureIdentifier.getShowCountryDefault()
			)],
			[SignatureDialogFields.ENVIRONMENT_DATE, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showDate, SignatureIdentifier.getShowDateDefault()
			)],
			[SignatureDialogFields.ENVIRONMENT_LOCALE, FunctionHelper.applyValue(
				parameter.appearance?.identifierElements?.showLocal, SignatureIdentifier.getShowLocalDefault()
			)]
		]);

		let identifierValue: Array<SignatureDialogFields> = [];
		for (let [field, value] of identifier.entries()) {
			if (value) {
				identifierValue.push(field);
			}
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SignatureDialogFields.CERTIFICATE)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-certificate">
							<Translate {...this.props} data="signature.general.signature.certificate.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-certificate"
									  value={FunctionHelper.applyValue(
										  parameter.keyName, AddSignature.getKeyNameDefault()
									  )}
									  itemTemplate={(option: CertificateOption) => {
										  if (option.separator) {
											  return null;
										  }

										  return (
											  <>
												  {option.value !== this.CertificateOptionNew &&
													  <>
														  <Icon
															  icon={FunctionHelper.applyValue(
																  option.password, false
															  ) ? "icon-locked" : "icon-unlocked"}
															  tooltip={
																  <Translate {...this.props} data={
																	  FunctionHelper.applyValue(
																		  option.password, false
																	  ) ?
																		  "signature.general.signature.certificate.protected" :
																		  "signature.general.signature.certificate.unlocked"
																  } namespace="dialog"/>
															  }/>
														  <Icon
															  icon={option.keyStoreName === "" ?
																  "icon-globe small" : "icon-user"}
															  tooltip={
																  <Translate {...this.props} data={
																	  option.keyStoreName === "" ?
																		  "signature.general.signature.certificate.global" :
																		  "signature.general.signature.certificate.user"
																  } namespace="dialog"/>
															  }/>
													  </>
												  }
												  {option.label}
											  </>
										  );
									  }}
									  valueTemplate={(option: CertificateOption) => {
										  if (!FunctionHelper.exists(option)) {
											  return null;
										  }

										  return (
											  <>
												  {option.value !== this.CertificateOptionNew &&
													  <>
														  <Icon
															  icon={FunctionHelper.applyValue(
																  option.password, false
															  ) ? " icon-locked" : " empty"}
															  tooltip={
																  <Translate {...this.props} data={
																	  "signature.general.signature.certificate.protected"
																  } namespace="dialog"/>
															  }/>
														  <Icon
															  icon={option.keyStoreName === "" ?
																  "icon-globe small" : "icon-user"}
															  tooltip={
																  <Translate {...this.props} data={
																	  option.keyStoreName === "" ?
																		  "signature.general.signature.certificate.global" :
																		  "signature.general.signature.certificate.user"
																  } namespace="dialog"/>
															  }/>
													  </>
												  }
												  {option.label}
											  </>
										  );
									  }}
									  onChange={(e) => this.onChange(
										  SignatureDialogFields.CERTIFICATE, e.value
									  )}
									  appendTo={document.getElementById("root")} options={certificates}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={
							 !this.state.fields?.contains(SignatureDialogFields.CERTIFICATE_PASSWORD) ||
							 !this.state.certifcatePasswordNeeded
						 }>
						<label className="p-col-12 p-sm-5" htmlFor="control-password">
							<Translate {...this.props} data="signature.general.signature.certificate.password"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<Password inputId="control-password" feedback={false} toggleMask={true}
									  value={FunctionHelper.applyValue(
										  parameter.keyPassword, AddSignature.getKeyPasswordDefault()
									  )}
									  onChange={(e) => this.onChange(
										  SignatureDialogFields.CERTIFICATE_PASSWORD, e.currentTarget.value
									  )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SignatureDialogFields.CERTIFICATE_MANUAL) ||
							 parameter.keyName !== this.CertificateOptionNew}>
						<label className="p-col-12 p-sm-5" htmlFor="control-certificate-manual">
							<Translate {...this.props} data="signature.general.signature.certificate.add.title"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<div className="p-inputgroup">
								<InputText id="control-certificate-manual"
										   value={FunctionHelper.applyValue(certificate?.subject, "")}
										   tooltip={certificate?.subject}
										   onFocus={() => this.setState({
											   showAddDialog: true
										   })}/>
								<Button className="p-inputgroup-addon" icon="icon icon-upload small"
										onClick={() => this.setState({
											showAddDialog: true
										})}
										tooltip={Translate.value(
											"signature.general.signature.certificate.select", {ns: "dialog"}
										)}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.showSelection || !this.state.fields?.contains(SignatureDialogFields.FIELD)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-field">
							<Translate {...this.props} data="signature.general.signature.field.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-field"
									  value={FunctionHelper.applyValue(
										  parameter.fieldName, AddSignature.getFieldNameDefault()
									  )}
									  onChange={(e) => this.onChange(
										  SignatureDialogFields.FIELD, e.value
									  )}
									  appendTo={document.getElementById("root")} options={signatures}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.fieldName !== this.SignatureOptionNew ||
						!this.state.fields?.contains(SignatureDialogFields.FIELD_NAME)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-field-name">
							<Translate {...this.props} data="signature.general.signature.field.name"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-field-name"
									   className={errors.has(SignatureDialogFields.FIELD_NAME) ? "p-invalid" : ""}
									   tooltip={errors.has(SignatureDialogFields.FIELD_NAME) ? Translate.value(
										   errors.get(SignatureDialogFields.FIELD_NAME)!,
										   {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter.newName, PortalAddSignature.getNewNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   SignatureDialogFields.FIELD_NAME, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(SignatureDialogFields.FIELD_NAME) ?
								Translate.value(
									errors.get(SignatureDialogFields.FIELD_NAME)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(SignatureDialogFields.LEVEL)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-level">
							<Translate {...this.props} data="signature.general.permissions.level.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-level"
									  value={FunctionHelper.applyValue(
										  parameter.certificationLevel, AddSignature.getCertificationLevelDefault()
									  )}
									  onChange={(e) => this.onChange(
										  SignatureDialogFields.LEVEL, e.value
									  )}
									  appendTo={document.getElementById("root")}
									  options={[
										  {
											  label: Translate.value(
												  "signature.general.permissions.level.none",
												  {ns: "dialog"}
											  ),
											  value: CertificationLevel.None
										  },
										  {
											  label: Translate.value(
												  "signature.general.permissions.level.not_allowed",
												  {ns: "dialog"}
											  ),
											  disabled: isCertified,
											  value: CertificationLevel.NoChanges
										  },
										  {
											  label: Translate.value(
												  "signature.general.permissions.level.filling",
												  {ns: "dialog"}
											  ),
											  disabled: isCertified,
											  value: CertificationLevel.FormFillingAndSignatures
										  },
										  {
											  label: Translate.value(
												  "signature.general.permissions.level.filling_and_annotation",
												  {ns: "dialog"}
											  ),
											  disabled: isCertified,
											  value: CertificationLevel.FormFillingAndSignaturesAndAnnotations
										  }
									  ]}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					!parameter.visible
				}>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(SignatureDialogFields.APPEARANCE)}>
						<label className="p-col-12 p-sm-5 large-label" htmlFor="control-appearance"
							   onClick={() => this.setState({
								   showAppearanceDialog: true
							   })}>
							<Translate {...this.props} data="signature.appearance.field" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 large-input p-grid">
							<div className="p-inputgroup">
								<div className="signature-preview inline"
									 onClick={() => this.setState({
										 showAppearanceDialog: true
									 })}>
									{this.state.imageFile.value !== "" &&
										<img alt={
											Translate.value("signature.appearance.alt", {ns: "dialog"})
										}
											 src={
												 "data:" + this.state.imageFile.mimetype +
												 ";base64," + this.state.imageFile.value
											 }
											 style={{
												 opacity: FunctionHelper.applyValue(
													 parameter.appearance?.image?.opacity,
													 SignatureImage.getOpacityDefault()
												 ) / 100,
												 maxWidth: "100%",
												 maxHeight: "200px"
											 }}/>
									}
									{this.state.imageFile.value === "" &&
										<p>
											<Translate {...this.props} data="signature.appearance.label"
													   namespace="dialog"/>
										</p>
									}
								</div>
								<Button className="p-inputgroup-addon" icon="icon icon-upload small"
										onClick={() => this.setState({
											showAppearanceDialog: true
										})}
										tooltip={Translate.value(
											"signature.appearance.select", {ns: "dialog"}
										)}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(SignatureDialogFields.REASON)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-reason">
							<Translate {...this.props} data="signature.general.fields.reason" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-reason"
									   value={FunctionHelper.applyValue(
										   parameter.reason, AddSignature.getReasonDefault()
									   )}
									   onInput={(e) => this.onChange(
										   SignatureDialogFields.REASON, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(SignatureDialogFields.LOCATION)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-location">
							<Translate {...this.props} data="signature.general.fields.location" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-location"
									   value={FunctionHelper.applyValue(
										   parameter.location, AddSignature.getLocationDefault()
									   )}
									   onInput={(e) => this.onChange(
										   SignatureDialogFields.LOCATION, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(SignatureDialogFields.CONTACT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-contact">
							<Translate {...this.props} data="signature.general.fields.contact" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-contact"
									   value={FunctionHelper.applyValue(
										   parameter.contact, AddSignature.getContactDefault()
									   )}
									   onInput={(e) => this.onChange(
										   SignatureDialogFields.CONTACT, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!parameter.visible || !this.state.fields?.contains(SignatureDialogFields.COMMON_NAME)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-show-common-name"/>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-show-common-name"
										 checked={FunctionHelper.applyValue(
											 parameter.appearance?.showCommonName,
											 AppearanceAdd.getShowCommonNameDefault()
										 )}
										 onChange={(e) => this.onChange(
											 SignatureDialogFields.COMMON_NAME, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-show-common-name">
								<Translate {...this.props} data="signature.appearance.common_name" namespace="dialog"/>
							</label>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!parameter.visible || !this.state.fields?.contains(SignatureDialogFields.IDENTIFIER_PREFIX)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-identifier-prefix">
							<Translate {...this.props} data="signature.identifier.prefix.label" namespace="dialog"/>
							<Info {...this.props} data="signature.identifier.prefix.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-identifier-prefix"
									   value={FunctionHelper.applyValue(
										   parameter.appearance?.identifierElements?.textSignedBy,
										   SignatureIdentifier.getTextSignedByDefault()
									   )}
									   onInput={(e) => this.onChange(
										   SignatureDialogFields.IDENTIFIER_PREFIX, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={!parameter.visible}>
						<label className="p-col-12 p-sm-5" htmlFor="control-identifier">
							<Translate {...this.props} data="signature.identifier.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<MultiSelect display="comma" inputId="control-identifier" value={identifierValue}
										 onChange={(e) => {
											 for (let key of Array.from(identifier.keys())) {
												 this.onChange(key as SignatureDialogFields, e.value.includes(key));
											 }
										 }}
										 options={[
											 {
												 label: Translate.value(
													 "signature.identifier.personal.signed_by",
													 {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.PERSONAL_SIGNED_BY
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.personal.name", {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.PERSONAL_NAME
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.personal.email", {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.PERSONAL_EMAIL
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.organisation.common_name",
													 {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.ORGANISATION_COMMON_NAME
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.organisation.unit",
													 {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.ORGANISATION_UNIT
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.organisation.name",
													 {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.ORGANISATION_NAME
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.environment.state",
													 {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.ENVIRONMENT_STATE
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.environment.country",
													 {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.ENVIRONMENT_COUNTRY
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.environment.date", {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.ENVIRONMENT_DATE
											 },
											 {
												 label: Translate.value(
													 "signature.identifier.environment.locale",
													 {ns: "dialog"}
												 ),
												 value: SignatureDialogFields.ENVIRONMENT_LOCALE
											 }
										 ]}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderExtendedTab(parameter: PortalAddSignature): ReactNode {
		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(SignatureDialogFields.MODIFICATION_NAME)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-modification-name">
							<Translate {...this.props} data="signature.extended.modification.name.label"
									   namespace="dialog"/>
							<Info {...this.props} data="signature.extended.modification.name.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id="control-modification-name"
									   value={FunctionHelper.applyValue(
										   parameter.appearance?.name, AppearanceAdd.getNameDefault()
									   )}
									   placeholder={Translate.value(
										   "signature.extended.modification.name.placeholder",
										   {ns: "dialog"}
									   )}
									   onInput={(e) => this.onChange(
										   SignatureDialogFields.MODIFICATION_NAME, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(SignatureDialogFields.MODIFICATION_IDENTIFIER)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-modification-identifier">
							<Translate {...this.props} data="signature.extended.modification.identifier.label"
									   namespace="dialog"/>
							<Info {...this.props} data="signature.extended.modification.identifier.info"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputText id="control-modification-identifier"
									   value={FunctionHelper.applyValue(
										   parameter.appearance?.identifier, AppearanceAdd.getIdentifierDefault()
									   )}
									   placeholder={Translate.value(
										   "signature.extended.modification.identifier.placeholder",
										   {ns: "dialog"}
									   )}
									   onInput={(e) => this.onChange(
										   SignatureDialogFields.MODIFICATION_IDENTIFIER, e.currentTarget.value
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalSignature | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalAddSignature = parameter!.add as PortalAddSignature;

		return (
			<>
				<div className="p-col-12 p-grid">
					<TabView activeIndex={this.state.panel}
							 onTabChange={(e) => this.setState({panel: e.index})}>
						<TabPanel header={Translate.value("signature.general.label", {ns: "dialog"})}>
							{this.renderGeneralTab(addParameter)}
						</TabPanel>
						<TabPanel header={Translate.value("signature.extended.label", {ns: "dialog"})}
								  headerStyle={addParameter.visible ? {} : {display: "none"}}>
							{addParameter.visible && this.renderExtendedTab(addParameter)}
						</TabPanel>
					</TabView>
				</div>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let errors: Map<SignatureDialogFields, string> = this.state.fieldErrors;
		let parameter: PortalSignature | undefined = this.state.parameter?.data;
		let username: string = FunctionHelper.applyValue(this.props.context.session.getUser()?.userName, "");

		let classList: Array<string> = ["signature-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
						resizable={false} closeOnEscape={true} header={this.renderHeader()}
						footer={this.renderFooter()} appendTo={document.getElementById("root")}>
					<div className="p-grid">
						{this.renderError()}
						<Helper {...this.props}>
							<Translate {...this.props} data={"dialog.signature.general"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						<Helper {...this.props} type={MessageType.WARNING}>
							<Translate {...this.props} data={"dialog.signature.info"} namespace={"help"}
									   markdown={true}/>
						</Helper>
						{this.renderContents()}
					</div>
				</Dialog>
				<SignatureAddCertificateDialog {...this.props} visible={this.state.showAddDialog}
											   certificate={FunctionHelper.applyValue(
												   parameter?.add?.signer?.keyPair?.certificate?.value,
												   CertificateFileData.getValueDefault()
											   )}
											   privateKey={FunctionHelper.applyValue(
												   parameter?.add?.signer?.keyPair?.privateKey?.value,
												   PrivateKeyFileData.getValueDefault()
											   )}
											   privateKeyPassword={FunctionHelper.applyValue(
												   parameter?.add?.signer?.keyPair?.privateKey?.password,
												   PrivateKeyFileData.getPasswordDefault()
											   )}
											   submit={(
												   certificate: CertificateEntry, privateKey?: string,
												   privateKeyPassword?: string
											   ) => {
												   errors.delete(SignatureDialogFields.CERTIFICATE);
												   this.onChange(
													   SignatureDialogFields.CERTIFICATE_MANUAL, certificate
												   );
												   this.onChange(SignatureDialogFields.PRIVATE_KEY, privateKey);
												   this.onChange(
													   SignatureDialogFields.PRIVATE_KEY_PASSWORD, privateKeyPassword
												   );
											   }}
											   hide={() => this.setState({
												   showAddDialog: false
											   })}/>
				<SignatureAppearanceDialog {...this.props} visible={this.state.showAppearanceDialog}
										   parameter={parameter?.add?.appearance?.toJson()}
										   imageFile={this.state.imageFile} textSignature={username}
										   submit={(parameter: AppearanceAdd, imageFile: File) => {
											   this.onChange(SignatureDialogFields.FILE, imageFile);
											   this.onChange(
												   SignatureDialogFields.OPACITY,
												   FunctionHelper.applyValue(
													   parameter.image?.opacity, SignatureImage.getOpacityDefault()
												   )
											   );
											   this.onChange(SignatureDialogFields.POSITION, parameter.position);
										   }}
										   hide={() => this.setState({
											   showAppearanceDialog: false
										   })}/>
			</>
		);
	}

	protected onChange(field: SignatureDialogFields, value: any): void {
		let parameter: PortalSignature = this.state.parameter!.data;
		let addParameter: PortalAddSignature = parameter!.add as PortalAddSignature;
		let errors: Map<SignatureDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case SignatureDialogFields.FIELD:
				addParameter.fieldName = value;
				addParameter.visible = true;

				if (value === this.SignatureOptionNew) {
					addParameter.visible = false;
					break;
				}

				let signature: Field | null = this.findSignature(value);
				if (FunctionHelper.exists(signature)) {
					if (!FunctionHelper.exists(addParameter.appearance)) {
						addParameter.appearance = new AppearanceAdd({
							position: {}
						} as AppearanceAddInterface);
					} else if (!FunctionHelper.exists(addParameter.appearance?.position)) {
						addParameter.appearance!.position = new SignaturePosition({});
					}

					addParameter.appearance!.page = signature!.annotation![0].page;
					let rectangle: MetadataRectangle =
						(signature!.annotation![0].positions![0] as AnnotationRectangle).rectangle!;
					addParameter.appearance!.position!.x = rectangle.x;
					addParameter.appearance!.position!.y = rectangle.y;
					addParameter.appearance!.position!.width = rectangle.width;
					addParameter.appearance!.position!.height = rectangle.height;
				}
				break;
			case SignatureDialogFields.FIELD_NAME:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.newName = value;
				break;
			case SignatureDialogFields.CERTIFICATE:
				if (FunctionHelper.applyValue(addParameter.signer?.keyPair?.certificate?.value, "") === "" &&
					value === this.CertificateOptionNew) {
					errors.set(field, "errors.fields.set");
				} else {
					errors.delete(field);
				}

				if (value === this.CertificateOptionNew) {
					this.setState({
						certifcatePasswordNeeded: false
					});

					addParameter.keyPassword = undefined;
				} else {
					let userCertificates = UserCertificates.fromJson(this.props.userCertificates.get());
					let certificateSet: boolean = false;
					for (let certificate of FunctionHelper.applyValue(
						userCertificates.certificates, UserCertificates.getCertificatesDefault()
					)) {
						if (certificate.aliasName === value && certificate.hasPrivateKey) {
							certificateSet = true;

							let passwordNeeded: boolean = certificate.hasPrivateKey! && !certificate.isPrivateKeyReadable!;
							if (passwordNeeded) {
								addParameter.keyPassword = "";
							}

							this.setState({
								certifcatePasswordNeeded: passwordNeeded
							});
							break;
						}
					}

					if (!certificateSet) {
						this.setState({
							certifcatePasswordNeeded: false
						});

						addParameter.keyPassword = undefined;
						value = this.CertificateOptionNew;
					}
				}

				addParameter.keyName = value;
				break;
			case SignatureDialogFields.CERTIFICATE_PASSWORD:
				addParameter.keyPassword = value;
				break;
			case SignatureDialogFields.CERTIFICATE_MANUAL:
				if (!FunctionHelper.exists(addParameter.signer)) {
					addParameter.signer = new SignerAdd({
						keyPair: {
							certificate: {}
						}
					} as SignerAddInterface);
				} else if (!FunctionHelper.exists(addParameter.signer?.keyPair)) {
					addParameter.signer!.keyPair = new KeyPair({
						certificate: {}
					} as KeyPairInterface);
				} else if (!FunctionHelper.exists(addParameter.signer?.keyPair.certificate)) {
					addParameter.signer!.keyPair.certificate = new CertificateFileData({});
				}

				addParameter.signer!.keyPair!.certificate!.value = value.certificate;
				break;
			case SignatureDialogFields.PRIVATE_KEY:
				if (!FunctionHelper.exists(addParameter.signer)) {
					addParameter.signer = new SignerAdd({
						keyPair: {
							privateKey: {}
						}
					} as SignerAddInterface);
				} else if (!FunctionHelper.exists(addParameter.signer?.keyPair)) {
					addParameter.signer!.keyPair = new KeyPair({
						certificate: {}
					} as KeyPairInterface);
				} else if (!FunctionHelper.exists(addParameter.signer?.keyPair.privateKey)) {
					addParameter.signer!.keyPair.privateKey = new PrivateKeyFileData({});
				}

				addParameter.signer!.keyPair!.privateKey!.value = value;
				break;
			case SignatureDialogFields.PRIVATE_KEY_PASSWORD:
				if (!FunctionHelper.exists(addParameter.signer)) {
					addParameter.signer = new SignerAdd({
						keyPair: {
							privateKey: {}
						}
					} as SignerAddInterface);
				} else if (!FunctionHelper.exists(addParameter.signer?.keyPair)) {
					addParameter.signer!.keyPair = new KeyPair({
						certificate: {}
					} as KeyPairInterface);
				} else if (!FunctionHelper.exists(addParameter.signer?.keyPair.privateKey)) {
					addParameter.signer!.keyPair.privateKey = new PrivateKeyFileData({});
				}

				addParameter.signer!.keyPair!.privateKey!.password = value;
				break;
			case SignatureDialogFields.FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let file: File = value;
				let fileDataType: PortalSignatureFileData = PortalSignatureFileData.fromJson({
					...file
				} as PortalSignatureFileData);

				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						image: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.image)) {
					addParameter.appearance!.image = new SignatureImage({});
				}

				addParameter.appearance!.image!.data = fileDataType;

				this.setState({
					imageFile: file,
					parameter: this.state.parameter
				});
				return;
			case SignatureDialogFields.OPACITY:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = SignatureImage.getOpacityDefault();
				}

				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						image: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.image)) {
					addParameter.appearance!.image = new SignatureImage({});
				}

				addParameter.appearance!.image!.opacity = value;
				break;
			case SignatureDialogFields.POSITION:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({});
				}

				addParameter.appearance!.position = value;
				break;
			case SignatureDialogFields.REASON:
				addParameter.reason = value;
				break;
			case SignatureDialogFields.LOCATION:
				addParameter.location = value;
				break;
			case SignatureDialogFields.CONTACT:
				addParameter.contact = value;
				break;
			case SignatureDialogFields.LEVEL:
				addParameter.certificationLevel = value;
				break;
			case SignatureDialogFields.PERSONAL_SIGNED_BY:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showSignedBy = value;
				break;
			case SignatureDialogFields.PERSONAL_NAME:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showName = value;
				break;
			case SignatureDialogFields.PERSONAL_EMAIL:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showMail = value;
				break;
			case SignatureDialogFields.ORGANISATION_COMMON_NAME:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showCommonName = value;
				break;
			case SignatureDialogFields.ORGANISATION_UNIT:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showOrganisationUnit = value;
				break;
			case SignatureDialogFields.ORGANISATION_NAME:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showOrganisationName = value;
				break;
			case SignatureDialogFields.ENVIRONMENT_COUNTRY:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showCountry = value;
				break;
			case SignatureDialogFields.ENVIRONMENT_DATE:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showDate = value;
				break;
			case SignatureDialogFields.ENVIRONMENT_LOCALE:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showLocal = value;
				break;
			case SignatureDialogFields.ENVIRONMENT_STATE:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({
						identifierElements: {}
					} as AppearanceAddInterface);
				} else if (!FunctionHelper.exists(addParameter.appearance?.identifierElements)) {
					addParameter.appearance!.identifierElements = new SignatureIdentifier({});
				}

				addParameter.appearance!.identifierElements!.showState = value;
				break;
			case SignatureDialogFields.MODIFICATION_IDENTIFIER:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({});
				}

				addParameter.appearance!.identifier = value;
				break;
			case SignatureDialogFields.MODIFICATION_NAME:
				if (!FunctionHelper.exists(addParameter.appearance)) {
					addParameter.appearance = new AppearanceAdd({});
				}

				addParameter.appearance!.name = value;
				break;
			case SignatureDialogFields.COMMON_NAME:
				addParameter.appearance!.showCommonName = value;
				break;
			case SignatureDialogFields.IDENTIFIER_PREFIX:
				addParameter.appearance!.identifierElements!.textSignedBy = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-signature small"}/>
					<Translate {...this.props} data="signature.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	private findSignature(name: string): Field | null {
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let signatures: Array<Field> = FunctionHelper.applyValue(
			portalDocument.metadata?.form?.field, FormDocument.getFieldDefault()
		);
		for (let signature of signatures) {
			if (signature.type !== "Sig") {
				continue;
			}

			if (signature.name === name) {
				return signature
			}
		}

		return null;
	}

	private buildFieldName(): string {
		let nameFound: boolean = false;
		let nameCounter: number = 0;
		let name: string = 'Signature';

		while (!nameFound) {
			nameCounter++;
			nameFound = !this.findSignature(name + nameCounter);
		}

		return name + nameCounter;
	};

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalSignature | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let addParameter: PortalAddSignature = parameter!.add as PortalAddSignature;
		addParameter.newName = this.buildFieldName();

		if (addParameter.appearance!.identifierElements!.textSignedBy === "") {
			addParameter.appearance!.identifierElements!.textSignedBy = Translate.value(
				"signature.identifier.prefix.default", {ns: "dialog"}
			);
		}

		let docMDP: DocMdp | undefined = FunctionHelper.applyValue(
			portalDocument.metadata?.information?.docMDP, MetadataInformation.getDocMDPDefault()
		);
		if (docMDP !== DocMdp.None) {
			addParameter.certificationLevel = CertificationLevel.None
		}

		let signatures: Array<Field> = []
		for (let formField of FunctionHelper.applyValue(
			portalDocument.metadata?.form?.field, FormDocument.getFieldDefault()
		)) {
			if (formField.type !== "Sig" || FunctionHelper.exists(formField.signature) ||
				!formField.annotation![0].writable) {
				continue;
			}

			signatures.push(formField);
		}

		this.setState({
			panel: 0,
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			signatures: signatures,
			showSelection: !addParameter.visible,
			certifcatePasswordNeeded: FunctionHelper.exists(addParameter.keyPassword)
		}, () => {
			this.onChange(SignatureDialogFields.CERTIFICATE, addParameter.keyName);
		});
	};

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		this.setState({
			parameter: this.state.parameter
		}, async () => {
			await super.submit();

			let parameter: PortalSignature = this.state.parameter!.data.clone();
			let signatureAddParameter: PortalAddSignature = parameter?.add as PortalAddSignature;

			if (!signatureAddParameter.visible ||
				signatureAddParameter.appearance?.image?.data?.value === "") {
				signatureAddParameter.appearance!.image = undefined;
			}

			if (!signatureAddParameter.visible) {
				signatureAddParameter.appearance = undefined;
			}

			if (signatureAddParameter.keyName !== this.CertificateOptionNew) {
				signatureAddParameter.signer = undefined;
			}

			if (FunctionHelper.applyValue(
				signatureAddParameter.fieldName, AddSignature.getFieldNameDefault()
			) === this.SignatureOptionNew) {
				signatureAddParameter.fieldName = (parameter?.add as PortalAddSignature).newName;
				signatureAddParameter.appendSignature = true;
			} else {
				signatureAddParameter.appendSignature = false;
			}

			let operationData: Signature = Signature.fromJson(parameter.toJson());
			let webServiceType: WebServiceType = WebServiceTypes.SIGNATURE;
			let documentManager: PortalDocumentManager = this.props.context.documentManager;
			let selection: Array<PortalDocument> = documentManager.getSelection();

			for (let portalDocument of selection) {
				try {
					await this.props.context.session.executeRequest({
						webServiceType: webServiceType,
						document: portalDocument,
						operation: operationData,
						settings: Settings.fromJson({
							pdfa: {
								pdfaEditProfile: this.props.pdfaEditProfile
							},
							signature: {
								signatureEditProfile: this.props.signatureEditProfile
							},
							compress: {
								compressProfile: this.props.compressProfile,
								compressObjects: {
									objectStreamSize: this.props.objectStreamSize
								}
							}
						} as SettingsInterface)
					});
				} catch (exception: any) {
					if (await this.onError(exception)) {
						await this.submit();
					}
					return;
				}

				let message: Message = new Message("success.signature.sign", MessageType.SUCCESS);
				message.context = {filename: '"' + portalDocument.getFileName() + '" '};
				this.props.context.getPage().addMessage(message);
			}

			this.resetMouseMode(documentManager.getSelection());
			this.hide();
		});
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.SIGNATURE;
	}

	protected getDefaultParameter(): Parameter<PortalSignature> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		let parameter: PortalSignature | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let addParameter: PortalAddSignature = parameter!.add as PortalAddSignature;

		this.setState({
			panel: 0,
			showSelection: !addParameter.visible,
			certifcatePasswordNeeded: FunctionHelper.exists(addParameter.keyPassword)
		}, () => {
			this.onChange(SignatureDialogFields.CERTIFICATE, addParameter.keyName);
		});
	}
}

const SignatureDialog = React.memo(SignatureDialogDefinition,
	(prevProps: Readonly<SignatureDialogProperties>, nextProps: Readonly<SignatureDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default SignatureDialog;